<script setup>
/**
 * @fileoverview LoadingSpinner - Reusable spinner/loading component
 * @description A versatile loading spinner with multiple sizes and color variants
 * @example
 * <LoadingSpinner size="md" color="primary" label="Loading content" />
 */
import { computed } from 'vue'
import { cn } from '@/lib/utils'

/**
 * @typedef {'sm' | 'md' | 'lg' | 'xl'} SpinnerSize
 * @typedef {'primary' | 'white' | 'muted'} SpinnerColor
 */

const props = defineProps({
  /**
   * Size of the spinner
   * @type {SpinnerSize}
   * @default 'md'
   */
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md', 'lg', 'xl'].includes(value)
  },
  /**
   * Color variant of the spinner
   * @type {SpinnerColor}
   * @default 'primary'
   */
  color: {
    type: String,
    default: 'primary',
    validator: value => ['primary', 'white', 'muted'].includes(value)
  },
  /**
   * Accessible label for screen readers
   * @type {string}
   */
  label: {
    type: String,
    default: 'Loading'
  }
})

/**
 * Size mappings for spinner dimensions
 * @type {Record<SpinnerSize, string>}
 */
const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
  xl: 'w-12 h-12'
}

/**
 * Color mappings for spinner stroke
 * @type {Record<SpinnerColor, string>}
 */
const colorClasses = {
  primary: 'text-indigo-600',
  white: 'text-white',
  muted: 'text-slate-400'
}

/**
 * Computed class string for the spinner
 * @type {import('vue').ComputedRef<string>}
 */
const spinnerClasses = computed(() =>
  cn('animate-spin', sizeClasses[props.size], colorClasses[props.color])
)
</script>

<template>
  <div role="status" aria-live="polite" aria-busy="true" class="inline-block">
    <svg
      :class="spinnerClasses"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
      <path
        class="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
    <span class="sr-only">{{ label }}</span>
  </div>
</template>
